import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'
import { dateLocale } from '../i18n/format'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import SearchBar from '../components/SearchBar'
import ExportButtons from '../components/ExportButtons'
import CellTooltip from '../components/CellTooltip'
import ForbeholdPanel from './Kampanjer/ForbeholdPanel'
import { Calendar, DollarSign, Car, X, History, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import SummaryCards from './Kampanjer/SummaryCards'
import api from '../services/api'
import { leasingCampaignsAPI } from '../services/api'
import { ColumnDefinition } from '../utils/exportToCsv'
import { formatNorwegianDate } from '../utils/dateUtils'
import { 
  getListeprisTooltip,
  getInitialTotalTooltip,
  getLeasePriceTooltip,
  getLeasingpris1Tooltip,
  getTotalpris1Tooltip,
  getAdjustmentsTooltip,
  getLeasingpris2Tooltip,
  getTotalpris2Tooltip,
  getMonthlyLeaseFactorTooltip
} from '../utils/leasingTooltips'
import { calculateTotalAdjustments } from '../utils/leasingCalculations'

/**
 * "Om leasingkampanjene og beregningene" (#636), verbatim from the issue with
 * its "[X] km per år" filled in. Standard mileage is set per segment
 * (Segment.BaseMileage over the 36-month term): 45 000 km for every segment
 * but småbiler, which have 30 000 km.
 *
 * Same two consumers as the loan tab's forbehold (#498): the panel renders a
 * <p> per paragraph, and the PDF export gets the joined string as
 * ExportMetadata.explanationText. CSV and Excel read no such field.
 *
 * The text lives in kampanjer:leasing.forbehold (#714) and is read at render.
 */

const formatRente = (
  value: string | number | null | undefined,
  { fallback = '-', suffix = '' }: { fallback?: string; suffix?: string } = {}
): string => {
  if (value == null) return fallback
  const n = parseFloat(value as string)
  if (Number.isNaN(n)) return fallback
  return `${(n * 100).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${suffix}`
}

interface LeasingResult {
  ResultID: number
  CampaignURL: string | null
  CampaignID: string | null
  CarID: number
  CarBrand: string
  CarModel: string
  CarPrice: string
  CarPriceURL: string | null
  BodyType: string | null
  Segment: string
  Segment_ShortName: string | null
  FuelType: string
  DriveTrain: string | null
  Battery_kwh: string | null
  Range_km: number | null
  Image: string | null
  DealerID: number
  DealerName: string
  Dealer_ShortName: string | null
  LeaseDurationMonths: number
  Mileage: number | null
  InterestRate: string
  InitialFeeOppCost: string | null
  InitialPay: string | null
  InitialFee: string | null
  InitialTotal: string | null
  LeasePrice: string | null
  TotalPrice1: string
  TotalLeasePrice1: string
  AdjustOppCost: string | null
  AdjustMetallic: string
  AdjustService: string
  AdjustTowingHitch: string
  AdjustMileage: string
  AdjustHighMileage: string
  AdjustManualCorrection: string
  CommentManualCorrection: string | null
  TotalPrice2: string
  MonthlyLeaseFactor: string | null
  TotalLeasePrice2: string
  WinterTyres: number
  Active: number
  CalculatedDate: string
  CreatedDate: string
  ModifiedDate: string
  LastUpdate: string
}

export default function LeasingCampaigns() {
  const { t } = useTranslation('kampanjer')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<LeasingResult[]>([])
  const [filteredResults, setFilteredResults] = useState<LeasingResult[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [activeOnly, _setActiveOnly] = useState(true)
  const [selectedSegment, setSelectedSegment] = useState<string>('')
  const [selectedFuelType, setSelectedFuelType] = useState<string>('')
  const [selectedResult, setSelectedResult] = useState<LeasingResult | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [campaignHistory, setCampaignHistory] = useState<LeasingResult[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  useEffect(() => {
    fetchResults()
  }, []) // Only fetch on initial load

  useEffect(() => {
    let filtered = results.filter(result =>
      (result.CarBrand?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (result.CarModel?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    )

    if (selectedSegment) {
      filtered = filtered.filter(r => r.Segment === selectedSegment)
    }

    if (selectedFuelType) {
      filtered = filtered.filter(r => r.FuelType === selectedFuelType)
    }

    // Sort by TotalPrice2 (min to max)
    filtered.sort((a, b) => {
      const priceA = parseFloat(a.TotalPrice2 || '0')
      const priceB = parseFloat(b.TotalPrice2 || '0')
      return priceA - priceB
    })

    setFilteredResults(filtered)
  }, [results, searchTerm, selectedSegment, selectedFuelType])

  const fetchResults = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await api.get('/public/results/', { 
        params: {
          skip: 0, 
          limit: 100, 
          active_only: activeOnly 
        }
      })
      
      // API returns {"data": [...]} structure
      const data = response.data?.data || []
      setResults(data)
    } catch (err: any) {
      console.error('Leasing campaigns API error:', err)
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('leasing.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount?: number | string | null) => {
    if (!amount) return '-'
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(numAmount)) return '-'
    return numAmount.toLocaleString('nb-NO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(dateLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const fetchCampaignHistory = async (campaignId: string) => {
    try {
      setHistoryLoading(true)
      setHistoryError(null)
      const response = await leasingCampaignsAPI.getCampaignHistory(campaignId)
      setCampaignHistory(response.data?.data || [])
    } catch (err: any) {
      console.error('Campaign history API error:', err)
      setHistoryError(localizeApiDetail(err.response?.data?.detail) || err.message || t('leasing.historyLoadFailed'))
    } finally {
      setHistoryLoading(false)
    }
  }

  // Helper function to safely parse numeric values
  const toNumber = (value: number | string | null | undefined): number => {
    if (value === null || value === undefined) return 0
    return typeof value === 'string' ? parseFloat(value) : value
  }

  // Sort handler
  const handleSort = (key: string) => {
    if (sortConfig?.key === key) {
      // Toggle: asc -> desc -> null (clear)
      if (sortConfig.direction === 'asc') {
        setSortConfig({ key, direction: 'desc' })
      } else {
        setSortConfig(null)
      }
    } else {
      // New column, start with ascending
      setSortConfig({ key, direction: 'asc' })
    }
  }

  // Get sort icon for column header
  const getSortIcon = (columnKey: string) => {
    if (sortConfig?.key === columnKey) {
      return sortConfig.direction === 'asc' ? 
        <ArrowUp className="w-4 h-4 inline ml-1" /> : 
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('')
    setSelectedSegment('')
    setSelectedFuelType('')
  }

  // Sorted results
  const sortedResults = useMemo(() => {
    // Apply default sort (TotalPrice2 ascending) to filtered results first
    let sorted = [...filteredResults].sort((a, b) => {
      const priceA = parseFloat(a.TotalPrice2 || '0')
      const priceB = parseFloat(b.TotalPrice2 || '0')
      return priceA - priceB
    })

    // Apply custom sort if configured
    if (!sortConfig) return sorted

    sorted = [...sorted].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (sortConfig.key) {
        case 'CarBrand':
          aVal = (a.CarBrand || '').toLowerCase()
          bVal = (b.CarBrand || '').toLowerCase()
          break
        case 'CarModel':
          aVal = (a.CarModel || '').toLowerCase()
          bVal = (b.CarModel || '').toLowerCase()
          break
        case 'Segment':
          aVal = (a.Segment || '').toLowerCase()
          bVal = (b.Segment || '').toLowerCase()
          break
        case 'Mileage':
          aVal = toNumber(a.Mileage)
          bVal = toNumber(b.Mileage)
          break
        case 'DealerName':
          aVal = (a.DealerName || '').toLowerCase()
          bVal = (b.DealerName || '').toLowerCase()
          break
        case 'InterestRate':
          aVal = toNumber(a.InterestRate)
          bVal = toNumber(b.InterestRate)
          break
        case 'CarPrice':
          aVal = toNumber(a.CarPrice)
          bVal = toNumber(b.CarPrice)
          break
        case 'InitialTotal':
          aVal = toNumber(a.InitialPay) + toNumber(a.InitialFee)
          bVal = toNumber(b.InitialPay) + toNumber(b.InitialFee)
          break
        case 'LeasePrice':
          aVal = toNumber(a.LeasePrice)
          bVal = toNumber(b.LeasePrice)
          break
        case 'TotalLeasePrice1':
          aVal = toNumber(a.TotalLeasePrice1)
          bVal = toNumber(b.TotalLeasePrice1)
          break
        case 'TotalPrice1':
          aVal = toNumber(a.TotalPrice1)
          bVal = toNumber(b.TotalPrice1)
          break
        case 'TotalAdjustments':
          aVal = calculateTotalAdjustments(a)
          bVal = calculateTotalAdjustments(b)
          break
        case 'TotalLeasePrice2':
          aVal = toNumber(a.TotalLeasePrice2)
          bVal = toNumber(b.TotalLeasePrice2)
          break
        case 'TotalPrice2':
          aVal = toNumber(a.TotalPrice2)
          bVal = toNumber(b.TotalPrice2)
          break
        case 'MonthlyLeaseFactor':
          aVal = toNumber(a.MonthlyLeaseFactor)
          bVal = toNumber(b.MonthlyLeaseFactor)
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [filteredResults, sortConfig])

  // Column definitions for export
  const leasingColumns: ColumnDefinition[] = [
    { key: 'CarBrand', label: t('leasing.col.merke') },
    { key: 'CarModel', label: t('leasing.col.modell') },
    { 
      key: 'Segment_ShortName', 
      label: t('leasing.col.segment'),
      format: (val, row) => val || (row as any)?.Segment || ''
    },
    { 
      key: 'Mileage', 
      label: t('leasing.col.mileage'),
      format: (val) => val ? t('leasing.kmPerYear', { km: toNumber(val).toLocaleString('nb-NO') }) : '-'
    },
    { 
      key: 'Dealer_ShortName', 
      label: t('leasing.col.forhandler'),
      format: (val, row) => val || (row as any)?.DealerName || ''
    },
    { 
      key: 'InterestRate', 
      label: t('leasing.col.rente'),
      format: (val) => val ? `${(toNumber(val) * 100).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})}` : '-'
    },
    { 
      key: 'CarPrice', 
      label: t('leasing.col.listepris'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'InitialTotal', 
      label: t('leasing.col.initialTotal'),
      format: (_val: any, row?: any) => {
        const sum = toNumber(row?.InitialPay) + toNumber(row?.InitialFee)
        return sum.toLocaleString('nb-NO', {maximumFractionDigits:0})
      }
    },
    { 
      key: 'LeasePrice', 
      label: t('leasing.col.leasePriceExport'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'TotalLeasePrice1', 
      label: t('leasing.col.leasingpris1'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'TotalPrice1', 
      label: t('leasing.col.totalPrice1'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'AdjustOppCost', 
      label: t('leasing.col.adjustOppCost'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'AdjustMetallic', 
      label: t('leasing.col.adjustMetallic'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'AdjustService', 
      label: t('leasing.col.adjustService'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'AdjustTowingHitch', 
      label: t('leasing.col.adjustTowingHitch'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'AdjustMileage', 
      label: t('leasing.col.adjustMileage'),
      format: (val: any, row?: any) => {
        const v = row && toNumber(row.AdjustHighMileage) !== 0 ? row.AdjustHighMileage : val
        return toNumber(v).toLocaleString('nb-NO', {maximumFractionDigits:0})
      }
    },
    { 
      key: 'AdjustManualCorrection', 
      label: t('leasing.col.adjustManual'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'TotalLeasePrice2', 
      label: t('leasing.col.leasingpris2'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'TotalPrice2', 
      label: t('leasing.col.totalPrice2'),
      format: (val) => toNumber(val).toLocaleString('nb-NO', {maximumFractionDigits:0})
    },
    { 
      key: 'MonthlyLeaseFactor', 
      label: t('leasing.col.monthlyFactor'),
      format: (val) => val ? toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2}) : '-'
    },
  ]

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  const uniqueSegments = Array.from(new Set(results.map(r => r.Segment).filter(Boolean)))

  const forbeholdTittel = t('leasing.forbehold.title')
  const forbeholdAvsnitt = t('leasing.forbehold.paragraphs', { returnObjects: true }) as string[]

  const openModal = (result: LeasingResult) => {
    setSelectedResult(result)
    setShowModal(true)
    setShowHistory(false)
    setCampaignHistory([])
    setHistoryError(null)
    // Fetch history if CampaignID exists
    if (result.CampaignID) {
      fetchCampaignHistory(result.CampaignID)
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedResult(null)
    setShowHistory(false)
    setCampaignHistory([])
    setHistoryError(null)
  }

  return (
    <div className="space-y-6">
      {/* First thing on the tab, above the heading (#636 AC1), as on
          Lånekampanjer. */}
      <ForbeholdPanel title={forbeholdTittel} paragraphs={forbeholdAvsnitt} testId="leasing-forbehold" />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('leasing.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('leasing.subtitle')}
          </p>
        </div>

      </div>

      {/* Summary — same three cards as Lånekampanjer (#632, #631), over the
          whole list rather than the filtered view. */}
      <SummaryCards
        cards={[
          { icon: Car, tone: 'primary', label: t('cards.totalCampaigns'), value: results.length },
          {
            icon: DollarSign,
            tone: 'green',
            label: t('cards.lowestMonthlyPrice'),
            value: results.length > 0 && results.some(r => r.TotalLeasePrice2)
              ? formatCurrency(Math.min(...results.filter(r => r.TotalLeasePrice2 && parseFloat(r.TotalLeasePrice2) > 0).map(r => parseFloat(r.TotalLeasePrice2))))
              : '-',
          },
          { icon: Calendar, tone: 'purple', label: t('cards.uniqueBrands'), value: new Set(results.map(r => r.CarBrand)).size },
        ]}
      />

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('leasing.searchPlaceholder')}
            />
          </div>
          
          <div>
            <select
              value={selectedSegment}
              onChange={(e) => setSelectedSegment(e.target.value)}
              className="input-field w-full"
            >
              <option value="">{t('leasing.allSegments')}</option>
              {uniqueSegments.map(segment => (
                <option key={segment} value={segment}>{segment}</option>
              ))}
            </select>
          </div>

        </div>
      </div>

      {/* Export and Sort Reset */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          {sortConfig && (
            <button 
              onClick={() => setSortConfig(null)}
              className="btn-secondary text-sm"
            >
              {t('resetSort')}
            </button>
          )}
          {(searchTerm || selectedSegment || selectedFuelType) && (
            <button 
              onClick={resetFilters}
              className="btn-secondary text-sm"
            >
              {t('resetFilter')}
            </button>
          )}
        </div>
        <ExportButtons
          data={sortedResults}
          columns={leasingColumns}
          metadata={{
            title: t('leasing.title'),
            dateRange: formatNorwegianDate(new Date()),
            // PDF only (#636 AC4): exportToPdf is the sole reader of this
            // field, which keeps the forbehold out of the CSV and Excel files.
            explanationText: `${forbeholdTittel}. ${forbeholdAvsnitt.join(' ')}`,
            filters: [
              activeOnly ? t('leasing.filterActiveOnly') : t('leasing.filterAllCampaigns'),
              selectedSegment ? t('leasing.filterSegment', { value: selectedSegment }) : '',
              selectedFuelType ? t('leasing.filterFuel', { value: selectedFuelType }) : ''
            ].filter(f => f),
          }}
          disabled={loading}
        />
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="data-table data-table-compact">
          <thead>
            <tr>
              <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('CarBrand')}>
                {t('leasing.col.merke')} {getSortIcon('CarBrand')}
              </th>
              <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('CarModel')}>
                {t('leasing.col.modell')} {getSortIcon('CarModel')}
              </th>
              <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('Segment')} title={t('leasing.colTitle.segment')}>
                {t('leasing.col.segment')} {getSortIcon('Segment')}
              </th>
              <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('Mileage')}>
                {t('leasing.col.mileage')} {getSortIcon('Mileage')}
              </th>
              <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('DealerName')} title={t('leasing.colTitle.forhandler')}>
                {t('leasing.col.forhandler')} {getSortIcon('DealerName')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('InterestRate')} title={t('leasing.colTitle.rente')}>
                {t('leasing.col.rente')} {getSortIcon('InterestRate')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('CarPrice')}>
                {t('leasing.col.listepris')} {getSortIcon('CarPrice')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('InitialTotal')} title={t('leasing.colTitle.initialTotal')}>
                {t('leasing.col.initialTotal')} {getSortIcon('InitialTotal')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('LeasePrice')} title={t('leasing.colTitle.leasePrice')}>
                {t('leasing.col.leasePrice')} {getSortIcon('LeasePrice')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('TotalLeasePrice1')}>
                {t('leasing.col.totalLeasePrice1')} {getSortIcon('TotalLeasePrice1')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('TotalPrice1')}>
                {t('leasing.col.totalPrice1')} {getSortIcon('TotalPrice1')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('TotalAdjustments')}>
                {t('leasing.col.adjustments')} {getSortIcon('TotalAdjustments')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('TotalLeasePrice2')}>
                {t('leasing.col.totalLeasePrice2')} {getSortIcon('TotalLeasePrice2')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('TotalPrice2')}>
                {t('leasing.col.totalPrice2')} {getSortIcon('TotalPrice2')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('MonthlyLeaseFactor')}>
                {t('leasing.col.factor')} {getSortIcon('MonthlyLeaseFactor')}
              </th>
              <th className="text-center">{t('action')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedResults.map((result, idx) => {
              return (
                // Zebra striping, same treatment as Nybilstatistikk (#497 AC3).
                <tr
                  key={result.ResultID}
                  className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                >
                  <td className="font-medium">{result.CarBrand}</td>
                  <td>
                    {result.CampaignURL ? (
                      <a 
                        href={result.CampaignURL} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        title={result.CampaignURL}
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {result.CarModel}
                      </a>
                    ) : (
                      result.CarModel
                    )}
                  </td>
                  <td>
                    <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-sm" title={result.Segment}>
                      {result.Segment_ShortName || result.Segment}
                    </span>
                  </td>
                  <td className="text-right">
                    {result.Mileage ? `${result.Mileage.toLocaleString('nb-NO')} km` : '-'}
                  </td>
                  <td title={result.DealerName}>{result.Dealer_ShortName || result.DealerName}</td>
                  <td className="text-right">
                    <span>{formatRente(result.InterestRate)}</span>
                  </td>
                  <td className="text-right font-medium">
                    <CellTooltip content={getListeprisTooltip(result)}>
                      {result.CarPriceURL ? (
                        <a
                          href={result.CarPriceURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {formatCurrency(result.CarPrice)}
                        </a>
                      ) : (
                        <span>{formatCurrency(result.CarPrice)}</span>
                      )}
                    </CellTooltip>
                  </td>
                  <td className="text-right">
                    <CellTooltip content={getInitialTotalTooltip(result)}>
                      <span>{(toNumber(result.InitialPay) + toNumber(result.InitialFee)).toLocaleString('nb-NO', { maximumFractionDigits: 0 })}</span>
                    </CellTooltip>
                  </td>
                  <td className="text-right">
                    <CellTooltip content={getLeasePriceTooltip(result)}>
                      <span>{formatCurrency(result.LeasePrice)}</span>
                    </CellTooltip>
                  </td>
                  <td className="text-right">
                    <CellTooltip content={getLeasingpris1Tooltip(result)}>
                      <span>{formatCurrency(result.TotalLeasePrice1)}</span>
                    </CellTooltip>
                  </td>
                  <td className="text-right">
                    <CellTooltip content={getTotalpris1Tooltip(result)}>
                      <span>{formatCurrency(result.TotalPrice1)}</span>
                    </CellTooltip>
                  </td>
                  <td className="text-right" data-testid="justeringer-celle">
                    {(() => {
                      const sum = calculateTotalAdjustments(result)
                      // Not formatCurrency: it renders 0 as '-', the old adjustment cells showed 0.
                      return (
                        <CellTooltip content={getAdjustmentsTooltip(result)} width={640}>
                          <span className={sum < 0 ? 'text-green-600 dark:text-green-400' : sum > 0 ? 'text-red-600 dark:text-red-400' : ''}>
                            {sum.toLocaleString('nb-NO', { maximumFractionDigits: 0 })}
                          </span>
                        </CellTooltip>
                      )
                    })()}
                  </td>
                  <td className="text-right font-semibold text-primary-600 dark:text-primary-400">
                    <CellTooltip content={getLeasingpris2Tooltip(result)}>
                      <span>{formatCurrency(result.TotalLeasePrice2)}</span>
                    </CellTooltip>
                  </td>
                  <td className="text-right font-semibold">
                    <CellTooltip content={getTotalpris2Tooltip(result)}>
                      <span>{formatCurrency(result.TotalPrice2)}</span>
                    </CellTooltip>
                  </td>
                  <td className="text-right">
                    <CellTooltip content={getMonthlyLeaseFactorTooltip(result)}>
                      <span>{result.MonthlyLeaseFactor ? parseFloat(result.MonthlyLeaseFactor).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2}) : '-'}</span>
                    </CellTooltip>
                  </td>
                  <td className="text-center">
                    <button
                      onClick={() => openModal(result)}
                      className="text-primary-600 dark:text-primary-400 hover:underline text-sm font-medium"
                    >
                      {t('details')}
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {sortedResults.length === 0 && (
          <div className="p-12 text-center">
            <Car className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {t('leasing.noResults')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              {t('leasing.noResultsHint')}
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white dark:bg-dark-card border-b border-gray-200 dark:border-dark-border p-6 flex justify-between items-start">
              <div className="flex gap-4 flex-1">
                {selectedResult.Image && (
                  <img 
                    src={selectedResult.Image} 
                    alt={`${selectedResult.CarBrand} ${selectedResult.CarModel}`}
                    className="w-32 h-24 object-cover rounded-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                )}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {selectedResult.CarBrand} {selectedResult.CarModel}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    {selectedResult.Segment} • {selectedResult.FuelType}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('leasing.modal.basicInfo')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailRow label={t('leasing.modal.forhandler')} value={selectedResult.DealerName} />
                  <DetailRow label={t('leasing.modal.varighet')} value={t('leasing.modal.durationMonths', { n: selectedResult.LeaseDurationMonths })} />
                  <DetailRow label={t('leasing.modal.karosseri')} value={selectedResult.BodyType || 'N/A'} />
                  <DetailRow label={t('leasing.modal.drivverk')} value={selectedResult.DriveTrain || 'N/A'} />
                  {selectedResult.Battery_kwh && (
                    <DetailRow label={t('leasing.modal.batteri')} value={`${selectedResult.Battery_kwh} kWh`} />
                  )}
                  {selectedResult.Range_km && (
                    <DetailRow label={t('leasing.modal.rekkevidde')} value={`${selectedResult.Range_km} km`} />
                  )}
                </div>
              </div>

              {/* Leasingdetaljer */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('leasing.modal.leasingDetails')}</h3>
                <div className="space-y-0">
                  <DetailRow label={t('leasing.modal.manedspris')} value={formatCurrency(selectedResult.LeasePrice)} />
                  <DetailRow label={t('leasing.modal.startleie')} value={formatCurrency(selectedResult.InitialPay)} />
                  <DetailRow label={t('leasing.modal.etableringsgebyr')} value={formatCurrency(selectedResult.InitialFee)} />
                  <DetailRow label={t('leasing.modal.leieperiode')} value={t('leasing.modal.leieperiodeValue', { n: selectedResult.LeaseDurationMonths })} />
                  <DetailRow label={t('leasing.modal.arligKjorelengde')} value={selectedResult.Mileage && selectedResult.LeaseDurationMonths ? `${Math.round(selectedResult.Mileage / selectedResult.LeaseDurationMonths * 12).toLocaleString('nb-NO')} km` : 'N/A'} />
                  <DetailRow label={t('leasing.modal.totalprisKampanje')} value={formatCurrency(selectedResult.TotalPrice1)} />
                  <DetailRow label={t('leasing.modal.totalManedsprisKampanje')} value={formatCurrency(selectedResult.TotalLeasePrice1)} />
                </div>
              </div>

              {/* Inkluderte faktorer */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('leasing.modal.includedFactors')}</h3>
                <div className="space-y-0">
                  <DetailRow
                    label={t('leasing.modal.inklMetallic')}
                    value={toNumber(selectedResult.AdjustMetallic) <= 0 ? t('leasing.modal.yes') : t('leasing.modal.no')}
                  />
                  {/* WinterTyres: DB convention is 1 = included, 0 = not included */}
                  <DetailRow
                    label={t('leasing.modal.inklVinterhjul')}
                    value={selectedResult.WinterTyres === 1 ? t('leasing.modal.yes') : t('leasing.modal.no')}
                  />
                  <DetailRow
                    label={t('leasing.modal.inklHengerfeste')}
                    value={toNumber(selectedResult.AdjustTowingHitch) < 0 ? t('leasing.modal.yes') : t('leasing.modal.no')}
                  />
                  <DetailRow
                    label={t('leasing.modal.inklServiceavtale')}
                    value={toNumber(selectedResult.AdjustService) < 0 ? t('leasing.modal.yes') : t('leasing.modal.no')}
                  />
                  <DetailRow
                    label={t('leasing.modal.manuellJustering')}
                    value={toNumber(selectedResult.AdjustManualCorrection) !== 0 ? t('leasing.modal.yes') : t('leasing.modal.no')}
                  />
                </div>
              </div>

              {/* Pricing */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('leasing.modal.prising')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailRow label={t('leasing.modal.listepris')} value={formatCurrency(selectedResult.CarPrice)} />
                  <DetailRow label={t('leasing.modal.rente')} value={formatRente(selectedResult.InterestRate, { suffix: ' %' })} />
                  {selectedResult.LeasePrice && (
                    <DetailRow label={t('leasing.modal.leasingpris')} value={formatCurrency(selectedResult.LeasePrice)} />
                  )}
                  <DetailRow label={t('leasing.modal.totalLeasingprisFor')} value={formatCurrency(selectedResult.TotalLeasePrice1)} />
                  <DetailRow label={t('leasing.modal.totalprisFor')} value={formatCurrency(selectedResult.TotalPrice1)} />
                </div>
              </div>

              {/* Adjustments */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('leasing.modal.adjustmentFactors')}</h3>
                <div className="space-y-2">
                  <AdjustmentRow 
                    label={t('leasing.modal.adjOppCost')}
                    value={selectedResult.AdjustOppCost} 
                  />
                  <AdjustmentRow 
                    label={t('leasing.modal.adjMetallic')} 
                    value={selectedResult.AdjustMetallic} 
                  />
                  <AdjustmentRow 
                    label={t('leasing.modal.adjService')} 
                    value={selectedResult.AdjustService} 
                  />
                  <AdjustmentRow 
                    label={t('leasing.modal.adjTowingHitch')} 
                    value={selectedResult.AdjustTowingHitch} 
                  />
                  <AdjustmentRow 
                    label={t('leasing.modal.adjLowKm')} 
                    value={selectedResult.AdjustMileage} 
                  />
                  <AdjustmentRow 
                    label={t('leasing.modal.adjHighKm')} 
                    value={selectedResult.AdjustHighMileage} 
                  />
                  <AdjustmentRow 
                    label={t('leasing.modal.adjManual')} 
                    value={selectedResult.AdjustManualCorrection} 
                  />
                  {selectedResult.CommentManualCorrection && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 italic mt-2">
                      {t('leasing.modal.comment', { comment: selectedResult.CommentManualCorrection })}
                    </p>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('leasing.modal.summary')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailRow label={t('leasing.modal.totalManedsprisJustert')} value={formatCurrency(selectedResult.TotalLeasePrice2)} />
                  <DetailRow label={t('leasing.modal.totalprisJustert')} value={formatCurrency(selectedResult.TotalPrice2)} />
                  {selectedResult.InitialFee && toNumber(selectedResult.InitialFee) > 0 && selectedResult.InitialFeeOppCost && (
                    <DetailRow
                      label={t('leasing.modal.oppCostRate')}
                      value={`${(parseFloat(selectedResult.InitialFeeOppCost) * 100).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %`}
                    />
                  )}
                </div>
              </div>

              {/* Final Pricing */}
              <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('leasing.modal.finalPrice')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('leasing.modal.manedspris')}</p>
                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                      {formatCurrency(selectedResult.TotalLeasePrice2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('leasing.modal.totalpris')}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(selectedResult.TotalPrice2)}
                    </p>
                  </div>
                  {selectedResult.MonthlyLeaseFactor && (
                    <div className="col-span-2 mt-2 pt-2 border-t border-primary-200 dark:border-primary-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('leasing.modal.manedsfaktor')}</p>
                      <p className="text-xl font-bold text-primary-600 dark:text-primary-400">
                        {parseFloat(selectedResult.MonthlyLeaseFactor).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Campaign History Section */}
              {selectedResult.CampaignID && (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <History className="w-5 h-5" />
                      {t('leasing.modal.history')}
                    </h3>
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className="text-primary-600 dark:text-primary-400 hover:underline text-sm font-medium"
                    >
                      {showHistory ? t('leasing.modal.hideHistory') : t('leasing.modal.showHistory')}
                    </button>
                  </div>
                  
                  {showHistory && (
                    <>
                      {historyLoading ? (
                        <div className="flex justify-center py-8">
                          <LoadingSpinner />
                        </div>
                      ) : historyError ? (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                          <p className="text-sm text-red-800 dark:text-red-200">{historyError}</p>
                        </div>
                      ) : campaignHistory.length === 0 ? (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t('leasing.modal.noHistory')}
                          </p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto border border-gray-200 dark:border-dark-border rounded-lg">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  {t('leasing.modal.histStatus')}
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  {t('leasing.modal.histCreated')}
                                </th>
                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  {t('leasing.modal.histMonthly')}
                                </th>
                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  {t('leasing.modal.histInitial')}
                                </th>
                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  {t('leasing.modal.histTotal')}
                                </th>
                                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  {t('leasing.modal.histRente')}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-dark-border">
                              {campaignHistory.map((item, index) => {
                                const isActive = item.Active === 1
                                const prevItem = index < campaignHistory.length - 1 ? campaignHistory[index + 1] : null
                                const priceChange = prevItem ? parseFloat(item.LeasePrice || '0') - parseFloat(prevItem.LeasePrice || '0') : 0
                                const initialTotalChange = prevItem ? parseFloat(item.InitialTotal || '0') - parseFloat(prevItem.InitialTotal || '0') : 0
                                
                                return (
                                  <tr 
                                    key={item.ResultID} 
                                    className={isActive ? 'bg-green-50 dark:bg-green-900/10' : ''}
                                  >
                                    <td className="px-3 py-3 whitespace-nowrap">
                                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                        isActive 
                                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                                      }`}>
                                        {isActive ? t('leasing.modal.active') : t('leasing.modal.priceCheck')}
                                      </span>
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                      {formatDate(item.CreatedDate)}
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-gray-100">
                                      {formatCurrency(item.LeasePrice || undefined)}
                                      {priceChange !== 0 && (
                                        <span className={`ml-2 text-xs ${priceChange > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                          {priceChange > 0 ? '+' : ''}{formatCurrency(priceChange)}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                                      {formatCurrency(item.InitialTotal || undefined)}
                                      {initialTotalChange !== 0 && (
                                        <span className={`ml-2 text-xs ${initialTotalChange > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                          {initialTotalChange > 0 ? '+' : ''}{formatCurrency(initialTotalChange)}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-gray-100">
                                      {formatCurrency(item.TotalLeasePrice2)}
                                    </td>
                                    <td className="px-3 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                                      {formatRente(item.InterestRate, { fallback: 'N/A', suffix: ' %' })}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                          {campaignHistory.length > 0 && (
                            <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                              {t('leasing.modal.versionsFound', { n: campaignHistory.length })}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-dark-bg border-t border-gray-200 dark:border-dark-border p-6 space-y-4">
              {selectedResult.CampaignURL && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-yellow-600 dark:text-yellow-400 mt-0.5">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                        {t('leasing.modal.externalSite')}
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-2">
                        {t('leasing.modal.link', { url: selectedResult.CampaignURL })}
                      </p>
                      <a
                        href={selectedResult.CampaignURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        <span>{t('leasing.modal.goToOffer')}</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              )}
              <button
                onClick={closeModal}
                className="btn-secondary w-full"
              >
                {t('leasing.modal.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-dark-border">
      <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{value}</span>
    </div>
  )
}

function AdjustmentRow({ label, value }: { label: string; value: string | null }) {
  const numValue = parseFloat(value || '0')
  const isPositive = numValue > 0
  const isNegative = numValue < 0
  
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-dark-border">
      <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      <span className={`text-sm font-medium ${
        isNegative ? 'text-green-600 dark:text-green-400' : 
        isPositive ? 'text-red-600 dark:text-red-400' : 
        'text-gray-900 dark:text-gray-100'
      }`}>
        {numValue !== 0 && (isPositive ? '+' : '')}{numValue.toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} kr
      </span>
    </div>
  )
}
